import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as Sentry from '@sentry/react';
import { CartProvider } from './context/CartContext';
import { ChatProvider } from './context/ChatContext';
import { BillingProvider } from './context/BillingContext';
import BottomNav from './components/BottomNav';
import DashboardView from './views/DashboardView';
import PortalView from './views/PortalView';
import ChatView from './views/ChatView';
import QuoteView from './components/commerce/QuoteView';
import FloatingChatWidget from './components/chat/FloatingChatWidget';
import LifeOS from './views/LifeOS';
import ModulesView from './views/ModulesView';
import { ModuleDetailView } from './views/ModulesView/components/ModuleDetailView';
import AuthView from './views/AuthView';
import BizBuildingProgramView from './views/BizBuildingProgramView';
import ProductivityView from './views/ProductivityView';
import PartnerView from './views/PartnerView';
import { syncManager } from './services/sync';
import { wsClient } from './services/WebSocketClient';
import { db } from './db';
import { config } from './config';
import { User, LogOut, Lock, Clock, QrCode } from 'lucide-react';
import { isPartner, isAdmin } from './utils/roleUtils';
import FinanceView from './views/FinanceView';
import ClientSelector from './components/common/ClientSelector';
import AppHeader from './components/layout/AppHeader';
import NotificationCenter from './components/notifications/NotificationCenter';
import SovereignExecutionHubView from './views/SovereignHub/SovereignExecutionHubView';
import CJMSView from './views/CJMSView';
import DomainManagement from './views/DomainManagement';
import WhatsAppAuthView from './views/WhatsAppAuthView';
import BillingHubView from './views/BillingHubView';
import ModuleStatusView from './views/ModuleStatusView';
import LandingPage from './views/LandingPage';
import { PublicReferralForm } from './views/PartnerView/PublicReferralForm';
import { GoalToGapAssessment } from './views/CJMSView/components/GoalToGapAssessment';
import { ProgramSelectionPage } from './views/CJMSView/components/ProgramSelectionPage';
import { Questionnaire } from './views/CJMSView/components/Questionnaire';
import ServicesPage from './components/static/ServicesPage';
import FaqPage from './components/static/FaqPage';
import AboutPage from './components/static/AboutPage';
import ContactPage from './components/static/ContactPage';
import ReferralPage from './components/static/ReferralPage';
import QuestionnairePage from './components/static/QuestionnairePage';
import { MilestoneTracker } from './views/CJMSView/components/MilestoneTracker';
import { GrowthDashboard } from './views/CJMSView/components/GrowthDashboard';
import MandateView from './views/MandateView';
import FilesView from './views/FilesView';

Sentry.init({
    dsn: config?.SENTRY_DSN,
    sendDefaultPii: true,
    enableLogs: true,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1,
    integrations: [
        Sentry.replayIntegration(),
        Sentry.feedbackIntegration()
    ],
});

interface UserProfile {
    email: string;
    role: string;
    sheetUrl?: string;
    [key: string]: any;
}

interface UserMetadata {
     email?: string;
     role?: string;
     currentProgram?: string;
     accessStatus?: string;
     feature_flags?: Record<string, boolean>;
     sheetUrl?: string;
     token?: string;
     path?: 'referral' | 'own-product' | null;
     [key: string]: any;
 }

const ViewWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="w-full h-full"
    >
        {children}
    </motion.div>
);

function App() {
    const [managedClient, setManagedClient] = useState<UserProfile | null>(null);
    const [user, setUser] = useState<UserProfile | null>(() => {
        const email = localStorage.getItem(config?.USER_EMAIL_KEY);
        const storedRole = localStorage.getItem(config?.USER_ROLE_KEY);

        if (!email) {return null;}

        // DEBUG: Log the role being loaded from localStorage
        console.log(`[App Init] Loading user: ${email}, storedRole: ${storedRole}`);

        const role = storedRole ? storedRole.toUpperCase() : 'CUSTOMER';
        return { email, role };
    });

    const [activeTab, setActiveTab] = useState<string>(() => 'portal');
    const [showSettings, setShowSettings] = useState(false);
    const [userMetadata, setUserMetadata] = useState<UserMetadata | null>(null);
    const [currentQuoteId, setCurrentQuoteId] = useState<string | null>(null);
    const [isNotifOpen, setIsNotifOpen] = useState(false);
    const [hasNotifications, setHasNotifications] = useState(false);
    const [activeExecution, setActiveExecution] = useState<{ moduleId: string; executionId: string } | null>(null);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const qId = params.get('quote');
        const referral = params.get('ref');

        if (qId) {
            setCurrentQuoteId(qId);
            setActiveTab('quote_checkout');
        }
        if (referral) {
            localStorage.setItem('REFERRED_BY', referral);
        }

        // Handle URL path-based routing (e.g., /referral-submit from LandingPage)
        const path = window.location.pathname.replace(/^\//, '');
        if (path && path !== '' && path !== 'auth') {
            setActiveTab(path);
        }
    }, []);

    useEffect(() => {
        if (user?.role === 'CUSTOMER') {
            setActiveTab('portal');
        } else if (user?.role === 'ADMIN' || user?.role === 'PARTNER') {
            setActiveTab('dashboard');
        } else if (user?.role === 'CLIENT') {
            setActiveTab('dashboard');
        }
    }, [user]);

    useEffect(() => {
        if (user) {
            config.USER_EMAIL = user.email;
            syncManager.fetchHistory();

const metaInterval = setInterval(() => {
                 db.user.toCollection().first().then((entry) => {
                     if (entry) {
                         setUserMetadata({
                             email: entry.email,
                             role: entry.role,
                             sheetUrl: entry.sheetUrl,
                             token: entry.token,
                             currentProgram: entry.currentProgram,
                             accessStatus: entry.networkStatus,
                             path: entry.path || null
                         } as UserMetadata);
                     }
                 });
             }, 5000);

            const notifCheck = setInterval(async () => {
                const entry = await db.tabData.where('tabName').equals('Scripts_Swap_File').first();
                const count = entry?.data?.length || 0;
                setHasNotifications(count > 0);
            }, 900000);

            let proactiveSync: ReturnType<typeof setInterval> | null = null;
            if (isAdmin(user.role) || isPartner(user.role)) {
                proactiveSync = setInterval(() => {
                    syncManager.fetchTabData('Scripts_Swap_File', config.ADMIN_EMAIL);
                }, 900000);
            }

            syncManager.initWebSocket();
            wsClient.connect();

            return () => {
                clearInterval(metaInterval);
                clearInterval(notifCheck);
                if (proactiveSync) {clearInterval(proactiveSync);}
                wsClient.disconnect();
            };
        }
    }, [user]);

    useEffect(() => {
        if (managedClient) {
            syncManager.fetchBusinessModules(managedClient.email);
        }
    }, [managedClient]);

    const handleAuthSuccess = (userData: UserProfile) => {
        console.log('[AuthSuccess] Raw userData from API:', userData);
        const normalizedUser = {
            ...userData,
            role: userData.role ? userData.role.toUpperCase() : 'CUSTOMER'
        };
        console.log('[AuthSuccess] Normalized user:', normalizedUser);
        setUser(normalizedUser);
        config.USER_EMAIL = normalizedUser.email;

const meta: UserMetadata = {
     email: normalizedUser.email,
     role: normalizedUser.role,
     sheetUrl: userData.sheetUrl || (userData as any).Client_Sheet_URL || null,
     token: (userData as any).token || null,
     currentProgram: (userData as any).currentProgram || null,
     path: (userData as any).path || null
 };

        db.user.put({
            id: 1,
            email: normalizedUser.email,
            role: normalizedUser.role,
            sheetUrl: meta.sheetUrl || undefined,
            token: meta.token || undefined,
            currentProgram: meta.currentProgram || undefined,
        } as any).then(() => {
            setUserMetadata(meta);
            // fetchHistory already called in the user useEffect above
        });
    };

const handleLogout = () => {
         localStorage.removeItem(config?.USER_EMAIL_KEY);
         localStorage.removeItem(config?.USER_ROLE_KEY);
         setUser(null);
     };

     const updateUserPath = (path: 'referral' | 'own-product') => {
         // Update local state
         setUserMetadata(prev => ({
             ...(prev || {}),
             path: path
         }));
         
         // Persist to database
         db.user.put({
             id: 1,
             path: path
         }).catch((error) => {
             console.error('Failed to update user path:', error);
         });
     };

    if (!user) {
        return <AuthView onAuthSuccess={handleAuthSuccess} />;
    }

    const renderView = () => {
        if (activeExecution) {
            return (
                <ViewWrapper>
                    <ModuleDetailView
                        moduleId={activeExecution.moduleId}
                        executionId={activeExecution.executionId}
                        onBack={() => setActiveExecution(null)}
                    />
                </ViewWrapper>
            );
        }

        switch (activeTab) {
            case 'dashboard':
                return <ViewWrapper><DashboardView /></ViewWrapper>;
            case 'productivity':
                return <ViewWrapper><ProductivityView /></ViewWrapper>;
            case 'portal':
                return <ViewWrapper><PortalView /></ViewWrapper>;
            case 'chat':
                return <ViewWrapper><ChatView user={user} userMetadata={(managedClient || userMetadata) as any} /></ViewWrapper>;
            case 'finance':
                return <ViewWrapper><FinanceView /></ViewWrapper>;
            case 'cjms':
                return <ViewWrapper><CJMSView /></ViewWrapper>;
            case 'sovereign_hub':
                return <ViewWrapper><SovereignExecutionHubView /></ViewWrapper>;
            case 'modules':
                return (
                    <ViewWrapper>
                        <ModulesView
                            onSelectModule={setActiveTab}
                            onModuleExecuted={(data) => setActiveExecution(data)}
                            userRole={user.role}
                        />
                    </ViewWrapper>
                );
            case 'partner':
                return <ViewWrapper><PartnerView /></ViewWrapper>;
            case 'bizbuilding':
                return <ViewWrapper><BizBuildingProgramView user={user} /></ViewWrapper>;
            case 'domains':
                return <ViewWrapper><DomainManagement /></ViewWrapper>;
            case 'whatsapp_auth':
                return <ViewWrapper><WhatsAppAuthView onAuthSuccess={() => setActiveTab('chat')} /></ViewWrapper>;
            case 'billing':
                return <ViewWrapper><BillingHubView managedClient={managedClient} /></ViewWrapper>;
            case 'builder':
                return <ViewWrapper><ModuleStatusView moduleId="builder" moduleLabel="Website Builder" description="AI-powered website generation and deployment." /></ViewWrapper>;
            case 'email':
                return <ViewWrapper><ModuleStatusView moduleId="email" moduleLabel="Email Service" description="Professional alias and SMTP infrastructure setup." /></ViewWrapper>;
            case 'gbp':
                return <ViewWrapper><ModuleStatusView moduleId="gbp" moduleLabel="Google Business Profile" description="Local SEO and business profile optimization." /></ViewWrapper>;
            case 'lifeos':
                return <ViewWrapper><LifeOS user={user} userMetadata={userMetadata} /></ViewWrapper>;
            case 'landing':
                return <ViewWrapper><LandingPage /></ViewWrapper>;
            case 'static/landing':
                return <ViewWrapper><LandingPage /></ViewWrapper>;
            case 'static/services':
                return <ViewWrapper><ServicesPage /></ViewWrapper>;
            case 'static/faq':
                return <ViewWrapper><FaqPage /></ViewWrapper>;
            case 'static/about':
                return <ViewWrapper><AboutPage /></ViewWrapper>;
            case 'static/contact':
                return <ViewWrapper><ContactPage /></ViewWrapper>;
            case 'static/referral':
                return <ViewWrapper><ReferralPage /></ViewWrapper>;
            case 'static/questionnaire':
                return <ViewWrapper><QuestionnairePage /></ViewWrapper>;
            case 'referral-submit':
                return <ViewWrapper><PublicReferralForm /></ViewWrapper>;
            case 'assessment':
                return <ViewWrapper><GoalToGapAssessment leadId={user.id} email={user.email || ''} onComplete={(a) => console.log('Assessment complete:', a)} onCancel={() => setActiveTab('portal')} /></ViewWrapper>;
            case 'program-select':
                return <ViewWrapper><ProgramSelectionPage leadId={user.id} email={user.email || ''} onComplete={(s) => console.log('Program selected:', s)} onCancel={() => setActiveTab('portal')} /></ViewWrapper>;
            case 'questionnaire':
                return <ViewWrapper><Questionnaire leadId={user.id} email={user.email || ''} onComplete={(r) => console.log('Questionnaire complete:', r)} onCancel={() => setActiveTab('portal')} /></ViewWrapper>;
            case 'milestone':
                return <ViewWrapper><MilestoneTracker leadId={user.id} email={user.email || ''} /></ViewWrapper>;
            case 'growth':
                return <ViewWrapper><GrowthDashboard leadId={user.id} email={user.email || ''} /></ViewWrapper>;
            case 'mandate':
                return <ViewWrapper><MandateView /></ViewWrapper>;
            case 'files':
                return <ViewWrapper><FilesView /></ViewWrapper>;
            case 'quote_checkout':
                return <ViewWrapper><QuoteView quoteId={currentQuoteId} /></ViewWrapper>;
            default:
                return <ViewWrapper><PortalView /></ViewWrapper>;
        }
    };

    const renderAccessOverlay = () => {
        if (!userMetadata || !userMetadata.accessStatus || userMetadata.accessStatus === 'Active' || user.role === 'ADMIN' || activeTab === 'billing') {return null;}

        return (
            <div className="fixed inset-0 z-[100] bg-slate-900/90 backdrop-blur-md flex items-center justify-center p-6 animate-in fade-in duration-500">
                <div className="bg-white rounded-[2.5rem] p-10 max-w-sm w-full text-center shadow-2xl animate-in zoom-in-95 duration-500">
                    <div className="w-20 h-20 bg-red-100 rounded-3xl flex items-center justify-center text-red-600 mx-auto mb-6">
                        {userMetadata.accessStatus === 'Expired' ? <Clock size={40} /> : <Lock size={40} />}
                    </div>
                    <h2 className="text-2xl font-black text-slate-800 mb-4 uppercase tracking-tight">
                        {userMetadata.accessStatus === 'Expired' ? 'Trial Expired' : 'Account Locked'}
                    </h2>
                    <p className="text-slate-500 font-medium leading-relaxed mb-8 text-sm">
                        {userMetadata.accessStatus === 'Expired'
                            ? "Your 14-day trial has ended. Upgrade to a Network Membership to unlock your tools and continue building your business."
                            : "Your account is currently locked due to a billing issue. Please update your payment method to restore access."}
                    </p>
                    <div className="space-y-3">
                        <button
                            onClick={() => setActiveTab('billing')}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-2xl font-black uppercase tracking-widest text-xs transition-all active:scale-95 shadow-xl shadow-blue-200"
                        >
                            {userMetadata.accessStatus === 'Expired' ? 'Upgrade Now' : 'Update Payment'}
                        </button>
                        <button
                            onClick={handleLogout}
                            className="w-full py-4 text-slate-400 font-black uppercase tracking-widest text-[10px]"
                        >
                            Sign Out
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <BillingProvider>
            <CartProvider>
                <ChatProvider currentUser={user}>
                    <div className="min-h-screen bg-slate-50 pb-24 font-sans text-slate-900 overflow-x-hidden">
                        {renderAccessOverlay()}
                        <AppHeader
                            user={user}
                            userMetadata={managedClient || userMetadata}
                            hasNotifications={hasNotifications}
                            onNotifClick={() => setIsNotifOpen(true)}
                            onSettingsClick={() => setShowSettings(!showSettings)}
                        />

                        {(isAdmin(user.role) || isPartner(user.role)) && (
                            <div className="px-6 py-2 bg-slate-100/50 border-b border-slate-200">
                                <ClientSelector
                                    currentRole={user.role}
                                    onClientSelected={setManagedClient}
                                />
                            </div>
                        )}

                        <main className="px-4 py-6 w-full mx-auto md:max-w-5xl relative">
                            <AnimatePresence mode="wait">
                                {renderView()}
                            </AnimatePresence>
                        </main>

                        <FloatingChatWidget currentPageTitle={activeTab.toUpperCase()} />
                        <BottomNav activeTab={activeTab} onTabChange={setActiveTab} hideFocus={user.role === 'CUSTOMER'} user={user} userPath={userMetadata?.path} />
                        <NotificationCenter isOpen={isNotifOpen} onClose={() => setIsNotifOpen(false)} />

                        {showSettings && (
                            <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-300">
                                <div onClick={() => setShowSettings(false)} className="absolute inset-0" />
                                <div className="relative bg-white w-full max-w-md rounded-t-[3rem] shadow-2xl p-8 pt-10 animate-in slide-in-from-bottom duration-500">
                                    <div className="w-12 h-1.5 bg-slate-200 rounded-full mx-auto mb-8" />
                                    <div className="flex items-center gap-4 mb-10">
                                        <div className="h-16 w-16 bg-blue-100/50 rounded-2xl flex items-center justify-center text-blue-700">
                                            <User size={32} />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-slate-800">{user.email}</h3>
                                            <div className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-black rounded-lg uppercase tracking-wider border border-blue-100 mt-1">
                                                {user.role} Member
                                            </div>
                                        </div>
                                    </div>
<div className="space-y-4">
                                     {/* Path Selection (Admin Only) */}
                                     <div>
                                         <label className="block text-sm font-medium text-slate-700 mb-1">
                                             Business Path
                                         </label>
                                         <div className="flex space-x-4">
                                             <label className="flex items-center space-x-2">
                                                 <input
                                                     type="radio"
                                                     value="referral"
                                                     checked={userMetadata?.path === 'referral'}
                                                     onChange={(e) => {
                                                         if (e.target.value === 'referral' || e.target.value === 'own-product') {
                                                             updateUserPath(e.target.value as 'referral' | 'own-product');
                                                         }
                                                     }}
                                                     className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                                 />
                                                 <span className="text-sm font-medium text-slate-700">Referral Focus</span>
                                             </label>
                                             <label className="flex items-center space-x-2">
                                                 <input
                                                     type="radio"
                                                     value="own-product"
                                                     checked={userMetadata?.path === 'own-product'}
                                                     onChange={(e) => {
                                                         if (e.target.value === 'referral' || e.target.value === 'own-product') {
                                                             updateUserPath(e.target.value as 'referral' | 'own-product');
                                                         }
                                                     }}
                                                     className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                                 />
                                                 <span className="text-sm font-medium text-slate-700">Product Focus</span>
                                             </label>
                                         </div>
                                         <p className="mt-1 text-xs text-slate-500">
                                             Select your primary business focus strategy
                                         </p>
                                     </div>
                                     {/* End Path Selection */}
                                 </div>
                                 <div className="space-y-2">
                                     <button
                                         onClick={() => {
                                             setActiveTab('whatsapp_auth');
                                             setShowSettings(false);
                                         }}
                                         className="w-full flex items-center gap-4 p-5 rounded-2xl text-blue-600 hover:bg-blue-50 transition-colors font-bold"
                                     >
                                         <div className="p-2 bg-blue-100/50 rounded-xl">
                                             <QrCode size={20} />
                                         </div>
                                         Link WhatsApp Device
                                     </button>
                                     <button
                                         onClick={handleLogout}
                                         className="w-full flex items-center gap-4 p-5 rounded-2xl text-red-600 hover:bg-red-50 transition-colors font-bold"
                                     >
                                         <div className="p-2 bg-red-100/50 rounded-xl">
                                             <LogOut size={20} />
                                         </div>
                                         Sign Out Control Center
                                     </button>
                                     <button
                                         onClick={() => setShowSettings(false)}
                                         className="w-full p-5 text-slate-400 font-bold"
                                     >
                                         Close
                                     </button>
                                 </div>
                                </div>
                            </div>
                        )}
                    </div>
                </ChatProvider>
            </CartProvider>
        </BillingProvider>
    );
}

export default App;